import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  IndianRupee, Users, TrendingUp, AlertCircle, Eye, Edit3,
  CheckCircle, XCircle, Clock, Search, Filter, Calendar, Send,
  FileText, ArrowUpRight, ArrowDownRight, BarChart3, X, Check, AlertTriangle,
  Download, Printer, FileSpreadsheet, Table as TableIcon, Plus, Trash2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { buildApiUrl } from '../../utils/apiSetup';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import DaArrearsReport from './DaArrearsReport';

const ArrearManagement = ({ initialTab = 'da-arrears' }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [arrears, setArrears] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    total_arrears: 0,
    pending_arrears: 0,
    paid_arrears: 0,
    total_pending_amount: 0,
    total_paid_amount: 0,
    this_month_arrears: 0,
    this_month_amount: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedArrear, setSelectedArrear] = useState(null);
  const [allEmployees, setAllEmployees] = useState([]);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [arrearForm, setArrearForm] = useState({
    employee_id: '',
    arrear_type: '',
    applicable_month: '',
    applicable_year: new Date().getFullYear(),
    amount: '',
    reason: '',
    remarks: ''
  });

  const arrearTypes = [
    'Increment Arrear',
    'Promotion Arrear',
    'Salary Revision',
    'DA Arrear',
    'Bonus Adjustment',
    'Manual Adjustment',
    'Other'
  ];

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const reasons = [
    'Delayed Increment',
    'Salary Correction',
    'Promotion',
    'Administrative Delay',
    'Manual Adjustment',
    'Court Order',
    'Other'
  ];

  const years = Array.from({ length: 11 }, (_, i) => new Date().getFullYear() - 5 + i);

  useEffect(() => {
    fetchDashboardData();
    fetchArrears();
  }, [statusFilter, typeFilter, monthFilter, yearFilter]);

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(buildApiUrl('/api/v1/arrears/dashboard/stats'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const stats = await response.json();
        setDashboardStats(stats);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  const fetchArrears = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('arrear_type', typeFilter);
      if (monthFilter !== 'all') params.append('month', monthFilter);
      if (yearFilter !== 'all') params.append('year', yearFilter);
      if (searchTerm) params.append('search', searchTerm);

      const response = await fetch(buildApiUrl(`/api/v1/arrears?${params}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setArrears(data);
      }
    } catch (error) {
      console.error('Error fetching arrears:', error);
      setError('Failed to load arrears');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllEmployees = async () => {
    try {
      const response = await fetch(buildApiUrl('/api/v1/benfish-employees/search?limit=1000'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const activeEmployees = (data.employees || []).filter(emp => emp.status === 'active');
        setAllEmployees(activeEmployees);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleCreateArrear = async () => {
    if (!arrearForm.employee_id || !arrearForm.arrear_type || !arrearForm.applicable_month ||
        !arrearForm.applicable_year || !arrearForm.amount || !arrearForm.reason) {
      setError('Please fill in all required fields');
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(buildApiUrl('/api/v1/arrears'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(arrearForm)
      });

      if (response.ok) {
        setSuccess('Arrear created successfully');
        setShowCreateModal(false);
        resetForm();
        fetchArrears();
        fetchDashboardData();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create arrear');
        setTimeout(() => setError(''), 3000);
      }
    } catch (error) {
      console.error('Error creating arrear:', error);
      setError('Failed to create arrear');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateArrear = async () => {
    if (!selectedArrear) return;

    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(`/api/v1/arrears/${selectedArrear.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(arrearForm)
      });

      if (response.ok) {
        setSuccess('Arrear updated successfully');
        setShowViewModal(false);
        setEditMode(false);
        setSelectedArrear(null);
        resetForm();
        fetchArrears();
        fetchDashboardData();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update arrear');
        setTimeout(() => setError(''), 3000);
      }
    } catch (error) {
      console.error('Error updating arrear:', error);
      setError('Failed to update arrear');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteArrear = async () => {
    if (!selectedArrear) return;

    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(`/api/v1/arrears/${selectedArrear.id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        setSuccess('Arrear deleted successfully');
        setShowDeleteConfirm(false);
        setShowViewModal(false);
        setSelectedArrear(null);
        fetchArrears();
        fetchDashboardData();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to delete arrear');
        setTimeout(() => setError(''), 3000);
      }
    } catch (error) {
      console.error('Error deleting arrear:', error);
      setError('Failed to delete arrear');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleViewArrear = (arrear) => {
    setSelectedArrear(arrear);
    setArrearForm({
      employee_id: arrear.employee_id,
      arrear_type: arrear.arrear_type,
      applicable_month: arrear.applicable_month,
      applicable_year: arrear.applicable_year,
      amount: arrear.amount,
      reason: arrear.reason,
      remarks: arrear.remarks || ''
    });
    setShowViewModal(true);
    setEditMode(false);
  };

  const handleEditArrear = (arrear) => {
    setSelectedArrear(arrear);
    setArrearForm({
      employee_id: arrear.employee_id,
      arrear_type: arrear.arrear_type,
      applicable_month: arrear.applicable_month,
      applicable_year: arrear.applicable_year,
      amount: arrear.amount,
      reason: arrear.reason,
      remarks: arrear.remarks || ''
    });
    setShowViewModal(true);
    setEditMode(true);
  };

  const resetForm = () => {
    setArrearForm({
      employee_id: '',
      arrear_type: '',
      applicable_month: '',
      applicable_year: new Date().getFullYear(),
      amount: '',
      reason: '',
      remarks: ''
    });
    setSelectedEmployee(null);
  };

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    setArrearForm({ ...arrearForm, employee_id: employee.id });
    setEmployeeSearchTerm('');
  };

  const filteredEmployees = allEmployees.filter(emp => {
    const search = employeeSearchTerm.toLowerCase();
    return (
      emp.emp_code?.toLowerCase().includes(search) ||
      emp.full_name?.toLowerCase().includes(search) ||
      emp.first_name?.toLowerCase().includes(search) ||
      emp.last_name?.toLowerCase().includes(search)
    );
  });

  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-sm font-medium mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-800">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );

  const renderDashboard = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        <StatCard
          title="Total Arrears"
          value={dashboardStats.total_arrears}
          icon={FileText}
          color="bg-blue-500"
        />
        <StatCard
          title="Pending Arrears"
          value={dashboardStats.pending_arrears}
          icon={Clock}
          color="bg-yellow-500"
        />
        <StatCard
          title="Paid Arrears"
          value={dashboardStats.paid_arrears}
          icon={CheckCircle}
          color="bg-green-500"
        />
        <StatCard
          title="Total Pending Amount"
          value={`₹${parseFloat(dashboardStats.total_pending_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
          icon={IndianRupee}
          color="bg-orange-500"
        />
        <StatCard
          title="Total Paid Amount"
          value={`₹${parseFloat(dashboardStats.total_paid_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
          icon={TrendingUp}
          color="bg-purple-500"
        />
        <StatCard
          title="This Month Arrears"
          value={dashboardStats.this_month_arrears}
          icon={Calendar}
          color="bg-indigo-500"
        />
        <StatCard
          title="This Month Amount"
          value={`₹${parseFloat(dashboardStats.this_month_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
          icon={ArrowUpRight}
          color="bg-pink-500"
        />
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => {
              resetForm();
              fetchAllEmployees();
              setShowCreateModal(true);
            }}
            className="flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Create Arrear
          </button>
          <button
            onClick={() => {
              setActiveTab('pending');
              setStatusFilter('pending');
            }}
            className="flex items-center justify-center gap-2 bg-yellow-600 text-white px-6 py-3 rounded-lg hover:bg-yellow-700 transition-colors"
          >
            <Clock className="w-5 h-5" />
            View Pending
          </button>
          <button
            onClick={() => {
              setActiveTab('paid');
              setStatusFilter('paid');
            }}
            className="flex items-center justify-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
          >
            <CheckCircle className="w-5 h-5" />
            View Paid
          </button>
        </div>
      </div>
    </div>
  );

  const renderArrearsTable = () => (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Types</option>
            {arrearTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <select
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Months</option>
            {months.map(month => (
              <option key={month} value={month}>{month}</option>
            ))}
          </select>
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Years</option>
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Arrear Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month/Year</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : arrears.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    No arrears found
                  </td>
                </tr>
              ) : (
                arrears.map((arrear) => (
                  <tr key={arrear.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {arrear.arrear_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      <div>
                        <div className="font-medium">{arrear.employee_name}</div>
                        <div className="text-xs text-gray-500">{arrear.employee_code}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {arrear.arrear_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {arrear.applicable_month} {arrear.applicable_year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ₹{parseFloat(arrear.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        arrear.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {arrear.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {formatDate(arrear.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewArrear(arrear)}
                          className="text-blue-600 hover:text-blue-800"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {arrear.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleEditArrear(arrear)}
                              className="text-green-600 hover:text-green-800"
                              title="Edit"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedArrear(arrear);
                                setShowDeleteConfirm(true);
                              }}
                              className="text-red-600 hover:text-red-800"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Arrear Management</h1>
        <p className="text-gray-600 mt-1">Calculate DA arrears for employees</p>
      </div>

      {/* Error/Success Messages */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center gap-2"
          >
            <AlertCircle className="w-5 h-5" />
            {error}
            <button onClick={() => setError('')} className="ml-auto">
              <X className="w-5 h-5" />
            </button>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded flex items-center gap-2"
          >
            <CheckCircle className="w-5 h-5" />
            {success}
            <button onClick={() => setSuccess('')} className="ml-auto">
              <X className="w-5 h-5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('da-arrears')}
          className={`px-4 py-2 font-medium flex items-center gap-2 ${
            activeTab === 'da-arrears'
              ? 'text-blue-600 border-b-2 border-blue-600 font-semibold'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <FileText className="w-4 h-4" />
          DA Arrears Report
        </button>
        {/* <button
          onClick={() => setActiveTab('dashboard')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'dashboard'
              ? 'text-blue-600 border-b-2 border-blue-600 font-semibold'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          Dashboard
        </button>
        <button
          onClick={() => {
            setActiveTab('pending');
            setStatusFilter('pending');
          }}
          className={`px-4 py-2 font-medium ${
            activeTab === 'pending'
              ? 'text-blue-600 border-b-2 border-blue-600 font-semibold'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          Pending Arrears
        </button>
        <button
          onClick={() => {
            setActiveTab('paid');
            setStatusFilter('paid');
          }}
          className={`px-4 py-2 font-medium ${
            activeTab === 'paid'
              ? 'text-blue-600 border-b-2 border-blue-600 font-semibold'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          Paid Arrears
        </button> */}
      </div>

      {/* Content */}
      {activeTab === 'da-arrears' ? (
        <DaArrearsReport />
      ) : activeTab === 'dashboard' ? (
        renderDashboard()
      ) : (
        renderArrearsTable()
      )}

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4"
            >
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-800">Create Arrear</h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="absolute top-6 right-6 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                {/* Employee Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search employee..."
                      value={employeeSearchTerm}
                      onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                      onFocus={() => fetchAllEmployees()}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {employeeSearchTerm && filteredEmployees.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredEmployees.map(emp => (
                          <div
                            key={emp.id}
                            onClick={() => handleEmployeeSelect(emp)}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                          >
                            <div className="font-medium">{emp.full_name || `${emp.first_name} ${emp.last_name}`}</div>
                            <div className="text-xs text-gray-500">{emp.emp_code}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {selectedEmployee && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                        <div className="font-medium">{selectedEmployee.full_name || `${selectedEmployee.first_name} ${selectedEmployee.last_name}`}</div>
                        <div className="text-sm text-gray-600">{selectedEmployee.emp_code}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Arrear Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Arrear Type *</label>
                  <select
                    value={arrearForm.arrear_type}
                    onChange={(e) => setArrearForm({ ...arrearForm, arrear_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select type</option>
                    {arrearTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                {/* Month and Year */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Applicable Month *</label>
                    <select
                      value={arrearForm.applicable_month}
                      onChange={(e) => setArrearForm({ ...arrearForm, applicable_month: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select month</option>
                      {months.map(month => (
                        <option key={month} value={month}>{month}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Applicable Year *</label>
                    <select
                      value={arrearForm.applicable_year}
                      onChange={(e) => setArrearForm({ ...arrearForm, applicable_year: parseInt(e.target.value) })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                    <input
                      type="number"
                      value={arrearForm.amount}
                      onChange={(e) => setArrearForm({ ...arrearForm, amount: e.target.value })}
                      className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter amount"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
                  <select
                    value={arrearForm.reason}
                    onChange={(e) => setArrearForm({ ...arrearForm, reason: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select reason</option>
                    {reasons.map(reason => (
                      <option key={reason} value={reason}>{reason}</option>
                    ))}
                  </select>
                </div>

                {/* Remarks */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                  <textarea
                    value={arrearForm.remarks}
                    onChange={(e) => setArrearForm({ ...arrearForm, remarks: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                    placeholder="Additional remarks (optional)"
                  />
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateArrear}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Arrear'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View/Edit Modal */}
      <AnimatePresence>
        {showViewModal && selectedArrear && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4"
            >
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-800">
                  {editMode ? 'Edit Arrear' : 'Arrear Details'}
                </h2>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setEditMode(false);
                    setSelectedArrear(null);
                  }}
                  className="absolute top-6 right-6 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                {/* Read-only fields when not editing */}
                {!editMode && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Arrear Number</label>
                        <p className="text-gray-900 font-medium">{selectedArrear.arrear_number}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          selectedArrear.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {selectedArrear.status}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Employee Code</label>
                        <p className="text-gray-900">{selectedArrear.employee_code}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Employee Name</label>
                        <p className="text-gray-900">{selectedArrear.employee_name}</p>
                      </div>
                    </div>
                    {selectedArrear.department && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                        <p className="text-gray-900">{selectedArrear.department}</p>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                      <p className="text-gray-900 font-medium text-xl">₹{parseFloat(selectedArrear.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Created Date</label>
                      <p className="text-gray-900">{formatDateTime(selectedArrear.created_at)}</p>
                    </div>
                    {selectedArrear.paid_date && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Paid Date</label>
                        <p className="text-gray-900">{formatDate(selectedArrear.paid_date)}</p>
                      </div>
                    )}
                    {selectedArrear.remarks && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                        <p className="text-gray-900">{selectedArrear.remarks}</p>
                      </div>
                    )}
                  </>
                )}

                {/* Editable fields when editing */}
                {editMode && (
                  <>
                    {/* Employee Selection - Read Only */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <div className="font-medium">{selectedArrear.employee_name}</div>
                        <div className="text-sm text-gray-600">{selectedArrear.employee_code}</div>
                      </div>
                    </div>

                    {/* Arrear Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Arrear Type *</label>
                      <select
                        value={arrearForm.arrear_type}
                        onChange={(e) => setArrearForm({ ...arrearForm, arrear_type: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {arrearTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    {/* Month and Year */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Applicable Month *</label>
                        <select
                          value={arrearForm.applicable_month}
                          onChange={(e) => setArrearForm({ ...arrearForm, applicable_month: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {months.map(month => (
                            <option key={month} value={month}>{month}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Applicable Year *</label>
                        <select
                          value={arrearForm.applicable_year}
                          onChange={(e) => setArrearForm({ ...arrearForm, applicable_year: parseInt(e.target.value) })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {years.map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Amount */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                        <input
                          type="number"
                          value={arrearForm.amount}
                          onChange={(e) => setArrearForm({ ...arrearForm, amount: e.target.value })}
                          className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>

                    {/* Reason */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
                      <select
                        value={arrearForm.reason}
                        onChange={(e) => setArrearForm({ ...arrearForm, reason: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {reasons.map(reason => (
                          <option key={reason} value={reason}>{reason}</option>
                        ))}
                      </select>
                    </div>

                    {/* Remarks */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                      <textarea
                        value={arrearForm.remarks}
                        onChange={(e) => setArrearForm({ ...arrearForm, remarks: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows="3"
                      />
                    </div>
                  </>
                )}
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                {!editMode && selectedArrear.status === 'pending' && (
                  <>
                    <button
                      onClick={() => setEditMode(true)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </>
                )}
                {editMode && (
                  <>
                    <button
                      onClick={() => setEditMode(false)}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdateArrear}
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {loading ? 'Updating...' : 'Update Arrear'}
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-md m-4"
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                  <h2 className="text-xl font-semibold text-gray-800">Confirm Delete</h2>
                </div>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to delete arrear {selectedArrear?.arrear_number}? This action cannot be undone.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteArrear}
                    disabled={loading}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    {loading ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ArrearManagement;
